import path from "path";
import { findPackageJson, safeReadJson } from "../utils/files.js";
import { analyzeDependencies } from "../analyze/dependencyAnalyzer.js";
import { loadPluginsForProject } from "../plugins/loader.js";
import { parseSemVer, compareSemVer, isMajorUpgrade, isMinorUpgrade, cleanVersion } from "./versionResolver.js";
import {
  getRnMatrixEntry,
  getLatestRnMatrixEntry,
  PACKAGE_COMPAT_MATRIX,
} from "./compatibilityMatrix.js";
import type { UpgradeRecommendation, PackageUpgradeInfo, RiskLevel } from "../models/UpgradeRecommendation.js";

/**
 * Computes a risk level for a given version transition.
 */
function computeRiskLevel(
  fromVersion: string,
  toVersion: string,
  breakingChangesCount: number
): { level: RiskLevel; reason: string } {
  if (isMajorUpgrade(fromVersion, toVersion)) {
    return {
      level: "HIGH",
      reason: "Major version bump — significant breaking changes likely.",
    };
  }

  const from = parseSemVer(fromVersion);
  const to = parseSemVer(toVersion);

  if (!from || !to) {
    return { level: "MEDIUM", reason: "Could not parse version numbers precisely." };
  }

  const minorDiff = to.minor - from.minor;
  if (minorDiff === 0) {
    return { level: "LOW", reason: "Patch-level upgrade only." };
  }
  if (minorDiff === 1 && breakingChangesCount === 0) {
    return { level: "LOW", reason: "One minor version bump with no known breaking changes." };
  }
  if (minorDiff <= 2 && breakingChangesCount <= 2) {
    return {
      level: "MEDIUM",
      reason: `${minorDiff} minor version(s) with ${breakingChangesCount} known breaking change(s).`,
    };
  }
  return {
    level: "HIGH",
    reason: `${minorDiff} minor version(s) gap with ${breakingChangesCount} known breaking change(s).`,
  };
}

/**
 * Checks installed packages against the compatibility matrix for the target RN version.
 */
function resolveAffectedPackages(
  allDeps: Record<string, string>,
  targetRnMinor: string
): PackageUpgradeInfo[] {
  const affected: PackageUpgradeInfo[] = [];

  for (const entry of PACKAGE_COMPAT_MATRIX) {
    const minVersion = entry.minVersionByRN[targetRnMinor];
    if (!minVersion) continue;

    // Find if user has this package (by prefix match)
    const installed = Object.entries(allDeps).find(([name]) =>
      name === entry.packagePrefix || name.startsWith(entry.packagePrefix + "/")
    );

    if (!installed) continue;

    const [installedName, installedSpec] = installed;
    const installedVer = parseSemVer(cleanVersion(installedSpec));
    const minVer = parseSemVer(minVersion);

    if (!installedVer || !minVer) continue;

    const cmp = compareSemVer(installedVer, minVer);
    if (cmp < 0) {
      affected.push({
        name: installedName,
        currentVersion: installedVer.raw,
        recommendedVersion: minVersion,
        breaking: isMinorUpgrade(installedVer.raw, minVersion) || isMajorUpgrade(installedVer.raw, minVersion),
        notes: entry.notes,
      });
    }
  }

  return affected.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Generates an upgrade recommendation report for the current project.
 */
export async function generateUpgradeRecommendation(
  cwd: string
): Promise<UpgradeRecommendation> {
  const packageJsonPath = findPackageJson(cwd);
  if (!packageJsonPath) {
    throw new Error("package.json not found.");
  }

  const pkg = safeReadJson(packageJsonPath);
  if (!pkg) throw new Error("package.json is corrupted or unreadable.");

  const projectRoot = path.dirname(packageJsonPath);
  const depAnalysis = analyzeDependencies(projectRoot, pkg as Record<string, unknown>);

  const allDeps = depAnalysis.allDeps;
  const currentRN = depAnalysis.reactNativeVersion;
  const currentExpo = cleanVersion(allDeps["expo"] || "") || null;

  // Target: latest known RN version
  const latestEntry = getLatestRnMatrixEntry();
  const targetRnMinor = latestEntry.rnMinor;
  const targetRnVersion = `${targetRnMinor}.0`;

  const currentVerParsed = parseSemVer(cleanVersion(currentRN));
  const targetVerParsed = parseSemVer(targetRnVersion);

  if (currentVerParsed && targetVerParsed && compareSemVer(currentVerParsed, targetVerParsed) >= 0) {
    return {
      currentRNVersion: currentRN,
      recommendedRNVersion: currentRN,
      currentExpoVersion: currentExpo,
      recommendedExpoVersion: currentExpo ? `SDK ${currentExpo.split(".")[0]}` : null,
      riskLevel: "LOW",
      riskReason: "You are already on the latest or a newer version of React Native supported by this version of Kyro.",
      affectedPackages: [],
      requiredActions: [
        "Your project is up-to-date with (or newer than) the matrix supported by this Kyro release.",
        "Run: kyro doctor to validate the project."
      ],
      generatedAt: new Date().toISOString(),
    };
  }

  // Recommended Expo SDK from matrix
  const targetExpo = currentExpo ? `SDK ${latestEntry.expoSdk}` : null;

  // Collect breaking changes for the path from current → latest
  const currentEntry = getRnMatrixEntry(currentRN);
  let allBreakingChanges: string[] = [];
  let intermediaryEntries: typeof import("./compatibilityMatrix.js").RN_EXPO_MATRIX = [];

  if (currentEntry) {
    const { RN_EXPO_MATRIX } = await import("./compatibilityMatrix.js");
    intermediaryEntries = RN_EXPO_MATRIX.filter((e) => {
      const [, em] = e.rnMinor.split(".");
      const [, cm] = (currentEntry.rnMinor).split(".");
      const [, lm] = targetRnMinor.split(".");
      const emi = parseInt(em);
      const cmi = parseInt(cm);
      const lmi = parseInt(lm);
      return emi > cmi && emi <= lmi;
    });
    allBreakingChanges = intermediaryEntries.flatMap((e) => e.breakingChanges);
  }

  const risk = computeRiskLevel(currentRN, targetRnVersion, allBreakingChanges.length);
  const affectedPackages = resolveAffectedPackages(allDeps, targetRnMinor);

  // Build required actions
  const requiredActions: string[] = [];
  if (allBreakingChanges.length > 0) {
    requiredActions.push(...allBreakingChanges.map((c) => `Review: ${c}`));
  }
  if (affectedPackages.length > 0) {
    requiredActions.push(
      `Update ${affectedPackages.length} incompatible package(s) before upgrading`
    );
  }
  if (latestEntry.newArchStable) {
    requiredActions.push("Evaluate enabling New Architecture (now stable in this version)");
  }
  requiredActions.push(`Run: kyro migrate ${currentRN} ${targetRnVersion}`);
  requiredActions.push("Run: kyro sync after upgrading");
  requiredActions.push("Run: kyro doctor to validate environment after upgrade");

  return {
    currentRNVersion: currentRN,
    recommendedRNVersion: targetRnVersion,
    currentExpoVersion: currentExpo,
    recommendedExpoVersion: targetExpo,
    riskLevel: risk.level,
    riskReason: risk.reason,
    affectedPackages,
    requiredActions,
    generatedAt: new Date().toISOString(),
  };
}
